import * as R from 'ramda'
import * as Nullable from '../../../Data/Nullable'
import * as Result from '../../../Data/Result'
import {update} from '../../../Data/ObjectHelper'
import {OfferAction} from './OfferActions'
import {
    AttachmentRow,
    OfferTab,
    PositionInput,
    PositionRow,
    PositionValidation
} from './OfferState'
import {InquiryWithDetails} from '../../../Entities/InquiryWithDetails'
import {
    OfferDownloadRequestIdentifier,
    OfferFeedback,
    OfferRequestIdentifier
} from './OfferFeedback'
import {decodeHttpResponse, HttpError} from '../../../Data/Http'
import * as Downloadable from '../../../Data/Downloadable'
import * as InquiryPositionAttachmentResource from '../../../Request/InquiryPositionAttachmentResource'
import {InquiryPositionAttachment} from '../../../Entities/InquiryPositionAttachment'
import * as InquiryPositionWithDetails
    from '../../../Entities/InquiryPositionWithDetails'
import {OfferDraftInquiry} from '../../../Entities/OfferDraftInquiry'
import {OfferDraft} from '../../../Entities/OfferDraft'
import {OfferDraftPosition} from '../../../Entities/OfferDraftPosition'
import {
    emptyStringToNullDecoder,
    mandatoryStringValidator,
    minimumValidator,
    stringToFixedPointNumberValidator,
    stringToIntValidator,
    validateInputField
} from '../../../Data/FormValidation'
import * as InquiryResource from '../../../Request/InquiryResource'
import {array, field, number, object} from 'json-bouncer'
import {filterMap} from '../../../Data/ArrayHelper'
// tslint:disable-next-line
import {Product} from '../../../Entities/Product'
import {InquiryPosition} from '../../../Entities/InquiryPosition'
import {Inquiry, inquiryDecoder} from '../../../Entities/Inquiry'
import {totalsDecoder} from '../../../Entities/Totals'
import {offerWithDetailsDecoder} from '../../../Entities/OfferWithDetails'
import {Session} from '../../../Data/Session'
import {formatPrice} from '../../../Data/NumberHelper'
import {
    initProductFinder,
    productFinderHttpResponseAction,
    productFinderReducer
} from '../../../Components/ProductFinder/ProductFinderReducer'
import {ProductFinderFeedback} from '../../../Components/ProductFinder/ProductFinderFeedback'
import {FeedbackLoop} from '../../../Data/FeedbackLoop'
import {calculatePrice} from '../../../Entities/PriceDefinition'

export function initOffer(inquiryWithDetails: InquiryWithDetails): OfferTab {
    return {
        inquiry: inquiryWithDetails.inquiry,
        editable: {type: 'Locked'},
        submissionAttempted: false,
        positionRows:
            initPositionRows(
                inquiryWithDetails.inquiryPositionsWithAttachments
            ),
        inquiryInput: initOfferAddressInput(inquiryWithDetails.inquiry),
        offers: inquiryWithDetails.offers,
        createOfferActionable: {type: 'Default'},
        offerCreationDialogOpen: false,
        offerCreationComment: '',
        offerCreatedToastOpen: false,
        productFinding: null,
        shippingCostsInput:
            formatPrice(
                inquiryWithDetails.inquiry.netShippingCosts !== null
                    ? inquiryWithDetails.inquiry.netShippingCosts
                    : 0
            ),
        shippingCostsValidation: null
    }
}

function initPositionRows(
    inquiryPositionsWithDetails: InquiryPositionWithDetails.InquiryPositionWithDetails[]
): PositionRow[] {
    return R.map(
        inquiryPositionWithDetails => {
            const {inquiryPosition, inquiryPositionAttachments, product} =
                inquiryPositionWithDetails
            return {
                id: inquiryPosition.id,
                inquiryPositionWithDetails,
                input: initPositionInput(inquiryPosition, product),
                validation: initPositionValidation(),
                attachmentRows:
                    R.map(
                        initAttachmentRow,
                        inquiryPositionAttachments,
                    ),
                attachmentsPopoverOpen: false,
            }
        },
        inquiryPositionsWithDetails
    )
}

function initPositionInput(
    inquiryPosition: InquiryPosition,
    product: Product | null
): PositionInput {
    return {
        name: inquiryPosition.name,
        amount: inquiryPosition.amount.toString(),
        price: inquiryPosition.totalNetPrice !== null
            ? formatPrice(inquiryPosition.totalNetPrice)
            : '',
        weight: inquiryPosition.weight.toString(),
        product,
        deleted: inquiryPosition.deleted !== null
    }
}

function initPositionValidation(): PositionValidation {
    return {
        name: null,
        amount: null,
        price: null,
        weight: null,
    }
}

function initOfferAddressInput(inquiry: Inquiry): OfferDraftInquiry {
    return {
        offerName1: inquiry.offerName1,
        offerName2: inquiry.offerName2,
        offerStreet: inquiry.offerStreet,
        offerHouseNumber: inquiry.offerHouseNumber,
        offerZipCode: inquiry.offerZipCode,
        offerCity: inquiry.offerCity,
        offerCountry: inquiry.offerCountry,
    }
}

function initAttachmentRow(inquiryPositionAttachment: InquiryPositionAttachment): AttachmentRow {
    return {
        inquiryPositionAttachment,
        download: {type: 'Default'},
    }
}

type OfferLoop =
    FeedbackLoop<OfferTab, OfferFeedback>

export function offerReducer(
    session: Session,
    inquiryNumber: number,
    action: OfferAction,
    state: OfferTab
): OfferLoop {
    switch (action.type) {
        case 'DownloadInquiryPositionAttachment':
            const {id, filename} = action.inquiryPositionAttachment
            return {
                state: updateAttachmentDownload(
                    {type: 'Loading'},
                    id,
                    state,
                ),
                feedback: [{
                    type: 'SendDownloadRequest',
                    identifier: {
                        type: 'InquiryPositionAttachmentDownload',
                        id,
                    },
                    httpRequest: InquiryPositionAttachmentResource.download(id),
                    filename,
                }],
            }
        case 'InquiryPositionAttachmentDownloadResponse':
            return {
                state: updateAttachmentDownload(
                    Downloadable.fromResult(action.result),
                    action.id,
                    state,
                ),
                feedback: [],
            }
        case 'Edit':
            return {
                state: update(
                    {
                        editable: {type: 'Pending', value: null},
                    },
                    state,
                ),
                feedback: [],
            }
        case 'ShippingCostsInputChanged':
            return {
                state: update({
                    shippingCostsInput: action.value,
                    shippingCostsValidation: validateInputField(
                        positionValidators.price,
                        state.submissionAttempted,
                        action.value,
                    ),
                }, state),
                feedback: []
            }
        case 'PositionNameChanged':
            return {
                state: updatePositionRow(
                    positionRow => update(
                        {
                            input: update(
                                {
                                    name: action.value,
                                },
                                positionRow.input,
                            ),
                            validation: update(
                                {
                                    name:
                                        validateInputField(
                                            positionValidators.name,
                                            state.submissionAttempted,
                                            action.value,
                                        ),
                                },
                                positionRow.validation,
                            ),
                        },
                        positionRow,
                    ),
                    action.id,
                    state,
                ),
                feedback: [],
            }
        case 'PositionAmountChanged':
            return {
                state: updatePositionRow(
                    positionRow => update(
                        {
                            input: update(
                                {amount: action.value},
                                positionRow.input,
                            ),
                            validation: update(
                                {
                                    amount:
                                        validateInputField(
                                            positionValidators.amount,
                                            state.submissionAttempted,
                                            action.value,
                                        ),
                                },
                                positionRow.validation,
                            ),
                        },
                        positionRow,
                    ),
                    action.id,
                    state,
                ),
                feedback: [],
            }
        case 'PositionWeightChanged':
            return {
                state: updatePositionRow(
                    positionRow => update(
                        {
                            input: update(
                                {weight: action.value},
                                positionRow.input,
                            ),
                            validation: update(
                                {
                                    weight:
                                        validateInputField(
                                            positionValidators.weight,
                                            state.submissionAttempted,
                                            action.value,
                                        ),
                                },
                                positionRow.validation,
                            ),
                        },
                        positionRow,
                    ),
                    action.id,
                    state,
                ),
                feedback: [],
            }
        case 'PositionPriceChanged':
            return {
                state: updatePositionRow(
                    positionRow => update(
                        {
                            input: update(
                                {price: action.value},
                                positionRow.input,
                            ),
                            validation: update(
                                {
                                    price:
                                        validateInputField(
                                            positionValidators.price,
                                            state.submissionAttempted,
                                            action.value,
                                        ),
                                },
                                positionRow.validation,
                            ),
                        },
                        positionRow,
                    ),
                    action.id,
                    state,
                ),
                feedback: [],
            }
        case 'InquiryInputChanged':
            return {
                state: update(
                    {
                        inquiryInput:
                            update(action.changes, state.inquiryInput),
                    },
                    state,
                ),
                feedback: [],
            }
        case 'Save':
            const validationOutcome = validate(state)
            if (validationOutcome.data !== null) {
                return {
                    state: update(
                        {
                            submissionAttempted: false,
                            editable: {type: 'Loading', value: null},
                        },
                        validationOutcome.state,
                    ),
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {type: 'InquiryOfferDraftUpdate'},
                        httpRequest:
                            InquiryResource.updateOfferDraft(
                                session.manufacturer.id,
                                inquiryNumber,
                                validationOutcome.data,
                            ),
                    }],
                }
            } else {
                return {
                    state: update(
                        {submissionAttempted: true},
                        validationOutcome.state,
                    ),
                    feedback: [],
                }
            }
        case 'InquiryShippingCostCalculationResponse':
            const ShippinCostsResult =
                decodeHttpResponse(
                    object({
                        calculatedShippingCosts: field('calculatedShippingCosts', number()),
                    }),
                    action.result,
                )
            if (ShippinCostsResult.type === 'Ok') {
                return {
                    state: update({
                            shippingCostsInput: formatPrice(ShippinCostsResult.value.calculatedShippingCosts)
                        },
                        state),
                    feedback: []
                }
            }
            return {
                state,
                feedback: []
            }
        case 'InquiryOfferDraftUpdateResponse': {
            const result =
                decodeHttpResponse(
                    object({
                        inquiry: field('inquiry', inquiryDecoder()),
                        inquiryPositionsWithAttachments:
                            field(
                                'inquiryPositionsWithAttachments',
                                array(InquiryPositionWithDetails.decoder()),
                            ),
                        totals: field('totals', totalsDecoder()),
                    }),
                    action.result,
                )
            return ((): OfferLoop => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    editable: {type: 'Locked'},
                                    inquiryInput:
                                        initOfferAddressInput(
                                            result.value.inquiry
                                        ),
                                    positionRows:
                                        initPositionRows(
                                            result.value.inquiryPositionsWithAttachments
                                        ),
                                    shippingCostsInput: formatPrice(
                                        result.value.inquiry.netShippingCosts !== null
                                            ? result.value.inquiry.netShippingCosts
                                            : 0),
                                },
                                state,
                            ),
                            feedback: [{
                                type: 'UpdateTotalsAfterPositionSave',
                                totals: result.value.totals
                            }],
                        }
                    case 'Err':
                        return {
                            state: update(
                                {
                                    editable: {
                                        type: 'Failure',
                                        error: result.error,
                                        value: null,
                                    },
                                },
                                state,
                            ),
                            feedback: [],
                        }
                }
            })()
        }
        case 'Discard':
            return {
                state: update(
                    {
                        editable: {type: 'Locked'},
                        positionRows:
                            filterMap(
                                positionRow => {
                                    if (positionRow.inquiryPositionWithDetails === null) {
                                        return null
                                    }
                                    return update(
                                        {
                                            input:
                                                initPositionInput(
                                                    positionRow.inquiryPositionWithDetails.inquiryPosition,
                                                    positionRow.inquiryPositionWithDetails.product,
                                                ),
                                            validation: initPositionValidation(),
                                        },
                                        positionRow,
                                    )
                                },
                                state.positionRows
                            ),
                        inquiryInput: initOfferAddressInput(state.inquiry),
                        submissionAttempted: false,
                    },
                    state,
                ),
                feedback: [],
            }
        case 'InquiryPositionBatchUpdateResponse':
            return {state, feedback: []}
        case 'AttachmentsPopoverToggle':
            return {
                state: updatePositionRow(
                    positionRow => update(
                        {
                            attachmentsPopoverOpen: action.open,
                        },
                        positionRow,
                    ),
                    action.id,
                    state,
                ),
                feedback: [],
            }
        case 'CreateOffer':
            return {
                state: update(
                    {
                        createOfferActionable: {type: 'Loading'},
                        offerCreationDialogOpen: false
                    },
                    state,
                ),
                feedback: [{
                    type: 'SendRequest',
                    identifier: {type: 'OfferCreate'},
                    httpRequest:
                        InquiryResource.createOffer(
                            session.manufacturer.id,
                            inquiryNumber,
                            state.offerCreationComment
                        ),
                }],
            }
        case 'OfferCreateResponse': {
            const result =
                decodeHttpResponse(
                    offerWithDetailsDecoder(),
                    action.result,
                )

            return ((): OfferLoop => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    createOfferActionable: {type: 'Default'},
                                    offerCreatedToastOpen: true,
                                    offers:
                                        R.append(
                                            result.value.offer,
                                            state.offers,
                                        ),
                                },
                                state,
                            ),
                            feedback: [{
                                type: 'UpdateHistoryAfterOfferCreation',
                                inquiryStatusChangeWithUsers: result.value.inquiryStatusChangeWithUsers,
                                inquiry: result.value.inquiry
                            }],
                        }
                    case 'Err':
                        return {
                            state: update(
                                {
                                    createOfferActionable: {
                                        type: 'Failure',
                                        error: result.error,
                                    }
                                },
                                state,
                            ),
                            feedback: [],
                        }
                }
            })()
        }
        case 'CloseOfferCreatedToast':
            return {
                state: update(
                    {
                        offerCreatedToastOpen: false,
                    },
                    state,
                ),
                feedback: [],
            }
        case 'ToogleOfferCreationDialog':
            return {
                state: update(
                    {
                        offerCreationDialogOpen: action.open,
                    },
                    state,
                ),
                feedback: [],
            }
        case 'ChangedOfferCreationComment':
            return {
                state: update(
                    {
                        offerCreationComment: action.value,
                    },
                    state,
                ),
                feedback: [],
            }
        case 'OfferPositionToBeDeletedToggle':
            return {
                state: update({
                        positionRows: filterMap(
                            positionRow => {
                                if (
                                    positionRow.inquiryPositionWithDetails === null &&
                                    positionRow.id === action.id &&
                                    action.deleted
                                ) {
                                    return null
                                } else if (positionRow.id === action.id) {
                                    positionRow = update({
                                            input: update({
                                                    deleted: action.deleted
                                                },
                                                positionRow.input)
                                        },
                                        positionRow)
                                    return positionRow
                                } else {
                                    return positionRow
                                }
                            },
                            state.positionRows
                        )
                    },
                    state),
                feedback: []
            }
        case 'CreateNewPosition':
            const newId = R.reduce(
                (previous, positionRow) =>
                    Math.max(previous, positionRow.id),
                1,
                state.positionRows
            ) + 1

            const newPositionRows =
                R.append(
                    {
                        id: newId,
                        inquiryPositionWithDetails: null,
                        attachmentRows: [],
                        input: {
                            name: '',
                            amount: '',
                            price: '',
                            weight: '',
                            product: null,
                            deleted: false
                        },
                        validation: {
                            name: null,
                            amount: null,
                            price: null,
                            weight: null
                        },
                        attachmentsPopoverOpen: false
                    },
                    state.positionRows
                )
            return {
                state: update({
                    positionRows: newPositionRows
                }, state),
                feedback: [],
            }
        case 'OpenProductFinder': {
            const productFinderLoop =
                initProductFinder(session, state.inquiry.customerId)
            return reduceProductFinderFeedback(
                productFinderLoop.feedback,
                update(
                    {
                        productFinding: {
                            positionRowId: action.id,
                            productFinderState: productFinderLoop.state
                        }
                    },
                    state,
                ),
            )
        }
        case 'RemoveReference':
            return {
                state: updatePositionRow(
                    positionRow => update(
                        {
                            input: update(
                                {product: null},
                                positionRow.input,
                            ),
                        },
                        positionRow,
                    ),
                    action.id,
                    state,
                ),
                feedback: [],
            }
        case 'ProductFinderModuleAction': {
            if (state.productFinding !== null) {
                const
                    productFinderLoop = productFinderReducer(
                        session,
                        state.inquiry.customerId,
                        state.productFinding.productFinderState,
                        action.subAction,
                    ),
                    newState =
                        update(
                            {
                                productFinding:
                                    update(
                                        {productFinderState: productFinderLoop.state},
                                        state.productFinding,
                                    ),
                            },
                            state,
                        )
                return reduceProductFinderFeedback(
                    productFinderLoop.feedback,
                    newState,
                )
            } else {
                return {state, feedback: []}
            }
        }
    }
}

function reduceProductFinderFeedback(
    productFinderFeedback: ProductFinderFeedback[],
    state: OfferTab,
): OfferLoop {
    return R.reduce<ProductFinderFeedback, OfferLoop>(
        (previous, feedback) => {
            switch (feedback.type) {
                case 'SendRequest':
                    return {
                        state: previous.state,
                        feedback:
                            R.append<OfferFeedback>(
                                {
                                    type: 'SendRequest',
                                    identifier: {
                                        type: 'ProductFinderModuleRequest',
                                        identifier: feedback.identifier,
                                    },
                                    httpRequest: feedback.httpRequest,
                                },
                                previous.feedback,
                            ),
                    }
                case 'ProductSelected':
                    if (state.productFinding === null) {
                        return previous
                    }
                    return {
                        state:
                            closeProductFinder(
                                updatePositionRow(
                                    positionRow => {
                                        const
                                            {product, priceDefinitions} =
                                                feedback.productWithPriceDefinitions,
                                            amount =
                                                Result.withDefault(
                                                    1,
                                                    positionValidators.amount(
                                                        positionRow.input.amount,
                                                    ),
                                                )
                                        return update(
                                            {
                                                input: update(
                                                    {
                                                        product,
                                                        name: product.name,
                                                        price:
                                                            formatPrice(
                                                                Result.withDefault(
                                                                    0,
                                                                    calculatePrice(
                                                                        priceDefinitions,
                                                                        amount
                                                                    ),
                                                                ),
                                                            ),
                                                        amount: amount.toString(),
                                                    },
                                                    positionRow.input,
                                                ),
                                            },
                                            positionRow,
                                        )
                                    },
                                    state.productFinding.positionRowId,
                                    state,
                                ),
                            ),
                        feedback: previous.feedback,
                    }
                case 'Canceled':
                    return {
                        state: closeProductFinder(state),
                        feedback: [],
                    }
            }
        },
        {state, feedback: []},
        productFinderFeedback,
    )
}

function closeProductFinder(state: OfferTab): OfferTab {
    return update({productFinding: null}, state)
}

interface ValidationOutcome {
    state: OfferTab,
    data: OfferDraft | null,
}

function validate(state: OfferTab): ValidationOutcome {
    const positionValidation =
        validatePositionRows(state.positionRows)
    const shippingCostValidation =
        shippingCostValidator(state.shippingCostsInput)
    return {
        state: update(
            {
                positionRows: positionValidation.positionRows,
                shippingCostsValidation:
                    Result.errorToNullable(shippingCostValidation)
            },
            state,
        ),
        data:
            shippingCostValidation.type === 'Ok' ?
                Nullable.map(
                    offerDraftPositions => ({
                        shippingCosts: shippingCostValidation.value,
                        offerDraftInquiry: state.inquiryInput,
                        offerDraftPositions,
                    }),
                    positionValidation.data,
                ) :
                null
    }
}

interface PositionRowValidationOutcome {
    positionRows: PositionRow[],
    data: OfferDraftPosition[] | null
}

function validatePositionRows(positionRows: PositionRow[]): PositionRowValidationOutcome {
    return R.reduce(
        (previous: PositionRowValidationOutcome, positionRow) => {
            const validationResult = validatePositionRow(positionRow)
            switch (validationResult.type) {
                case 'Ok':
                    return {
                        positionRows:
                            R.append(
                                positionRow,
                                previous.positionRows,
                            ),
                        data:
                            Nullable.map(
                                data =>
                                    R.append(
                                        validationResult.value,
                                        data
                                    ),
                                previous.data,
                            ),
                    }
                case 'Err':
                    return {
                        positionRows:
                            R.append(
                                update(
                                    {validation: validationResult.error},
                                    positionRow,
                                ),
                                previous.positionRows,
                            ),
                        data: null,
                    }
            }
        },
        {positionRows: [], data: []},
        positionRows,
    )
}

const positionValidators = {
    name: mandatoryStringValidator(),
    amount:
        Result.compose(
            minimumValidator(1),
            Result.compose(
                stringToIntValidator(),
                mandatoryStringValidator(),
            ),
        ),
    price:
        emptyStringToNullDecoder(
            Result.compose(
                minimumValidator(0),
                stringToFixedPointNumberValidator(2)
            ),
        ),
    weight:
        Result.compose(
            minimumValidator(0),
            stringToIntValidator(),
        ),
}

const shippingCostValidator = Result.compose(
    minimumValidator(0),
    stringToFixedPointNumberValidator(2),
)

function validatePositionRow(positionRow: PositionRow): Result.Result<PositionValidation, OfferDraftPosition> {
    const
        {input} = positionRow,
        validationResult = {
            name: positionValidators.name(input.name),
            amount: positionValidators.amount(input.amount),
            price: positionValidators.price(input.price),
            weight: positionValidators.weight(input.weight),
        }
    if (validationResult.name.type === 'Ok' &&
        validationResult.amount.type === 'Ok' &&
        validationResult.price.type === 'Ok' &&
        validationResult.weight.type === 'Ok'
    ) {
        return {
            type: 'Ok',
            value: {
                id: (positionRow.inquiryPositionWithDetails !== null) ? positionRow.id : null,
                name: validationResult.name.value,
                amount: validationResult.amount.value,
                price: validationResult.price.value,
                weight: validationResult.weight.value,
                productId: input.product !== null ? input.product.productId : null,
                deleted: positionRow.input.deleted
            }
        }
    } else {
        return {
            type: 'Err',
            error: {
                name: Result.errorToNullable(validationResult.name),
                amount: Result.errorToNullable(validationResult.amount),
                price: Result.errorToNullable(validationResult.price),
                weight: Result.errorToNullable(validationResult.weight),
            }
        }
    }
}

function updatePositionRow(
    fn: (positionRow: PositionRow) => PositionRow,
    id: number,
    state: OfferTab
): OfferTab {
    return update(
        {
            positionRows:
                R.map(
                    positionRow =>
                        positionRow.id === id
                            ? fn(positionRow)
                            : positionRow,
                    state.positionRows,
                ),
        },
        state,
    )
}

function updateAttachmentDownload(
    download: Downloadable.Downloadable,
    id: number,
    state: OfferTab
): OfferTab {
    return update(
        {
            positionRows:
                R.map(
                    positionRow =>
                        update(
                            {
                                attachmentRows:
                                    R.map(
                                        attachmentRow =>
                                            attachmentRow.inquiryPositionAttachment.id === id
                                                ? update({download}, attachmentRow)
                                                : attachmentRow,
                                        positionRow.attachmentRows,
                                    )
                            },
                            positionRow,
                        ),
                    state.positionRows
                ),
        },
        state,
    )
}

export function offerResponseAction(
    identifier: OfferRequestIdentifier,
    result: Result.Result<HttpError, string>
): OfferAction {
    switch (identifier.type) {
        case 'InquiryPositionBatchUpdate':
            return {
                type: 'InquiryPositionBatchUpdateResponse',
                result,
            }
        case 'InquiryOfferDraftUpdate':
            return {
                type: 'InquiryOfferDraftUpdateResponse',
                result,
            }
        case 'OfferCreate':
            return {
                type: 'OfferCreateResponse',
                result,
            }
        case 'ProductFinderModuleRequest':
            return {
                type: 'ProductFinderModuleAction',
                subAction:
                    productFinderHttpResponseAction(
                        identifier.identifier,
                        result,
                    ),
            }
    }
}

export function offerDownloadResponseAction(
    identifier: OfferDownloadRequestIdentifier,
    result: Result.Result<HttpError, null>
): OfferAction {
    switch (identifier.type) {
        case 'InquiryPositionAttachmentDownload':
            return {
                type: 'InquiryPositionAttachmentDownloadResponse',
                id: identifier.id,
                result,
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/InquiryDetails/Offer/OfferReducer.ts